import { useTranslation } from '@shared/core/i18n';
import type { GraphErrorTranslations } from '@locales/generated';

type ValidCode = keyof GraphErrorTranslations['code'];

const UNKNOWN_ERROR_CODE: ValidCode = 'UNKNOWN';

/**
 * Helper to resolve translation value which can be either a string or a function
 */
const resolveTranslation = (value: string | (() => string)): string => {
  return typeof value === 'function' ? value() : value;
};

/**
 * Hook to translate error codes using the graphError namespace
 */
export const useTranslateGraphError = () => {
  const { t } = useTranslation('graphError');

  /**
   * Translate error code to i18n message
   * Returns undefined if the code is UNKNOWN or translation not found
   *
   * @see doc/feature-plans/kyc-failure-messaging.md for implementation details
   */
  const translateGraphErrorCode = (code: string | undefined): string | undefined => {
    if (code === undefined) return undefined;
    if (code === UNKNOWN_ERROR_CODE) return undefined;

    try {
      // Infer valid code keys from GraphErrorTranslations.code type
      const result = t('code', <ValidCode>code);
      const translated = resolveTranslation(result);
      const customerSupport = t('shared', 'contactUs')({ email: 'support@withjoy.com' });
      const composite = `${translated} ${customerSupport}`;

      // If t returns the key unchanged, translation wasn't found
      return translated === code ? undefined : composite;
    } catch {
      return undefined;
    }
  };

  /**
   * Translate error code with fallback for UNKNOWN or missing translations
   * Always returns a message (uses fallback for UNKNOWN/missing)
   *
   * @see doc/feature-plans/kyc-failure-messaging.md for implementation details
   */
  const translateGraphErrorCodeOrUnknown = (code: string | undefined, fallback?: string): string => {
    if (code === undefined || code === UNKNOWN_ERROR_CODE) {
      const result = t('code', UNKNOWN_ERROR_CODE);
      return fallback ?? resolveTranslation(result);
    }
    return translateGraphErrorCode(code) ?? fallback ?? code;
  };

  return {
    translateGraphErrorCode,
    translateGraphErrorCodeOrUnknown,
  };
};
